/**
 * Tâche 1316 (Eric 2026-06-30) — Journal du dictionnaire.
 *
 * Totaux en haut, liste chronologique en dessous (choix d'Eric). Réservé aux
 * administrateurs : le serveur le refuse aux autres, l'écran n'est qu'un
 * second garde-fou.
 */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";

import { API_BASE as API } from "../lib/apiBase";
import { useLang } from "../contexts/LanguageContext";

const Carte = ({ titre, lignes, testid, vide, libelleTerme }) => (
  <div className="rounded-xl border border-zinc-200 bg-white p-4" data-testid={testid}>
    <h3 className="text-xs font-bold uppercase tracking-wide text-zinc-600">{titre}</h3>
    {lignes.length === 0 ? (
      <p className="mt-2 text-[11px] text-zinc-500">{vide}</p>
    ) : (
      <ul className="mt-2 space-y-1">
        {lignes.slice(0, 6).map((l) => (
          <li key={l.key || l.label} className="flex items-baseline justify-between gap-3 text-xs">
            <span className="text-zinc-800 truncate">{l.label}</span>
            <span className="font-bold text-zinc-900 whitespace-nowrap">
              {l.count}
              {typeof l.terms_count === "number" && (
                <span className="font-normal text-zinc-500"> · {l.terms_count} {libelleTerme}</span>
              )}
            </span>
          </li>
        ))}
      </ul>
    )}
  </div>
);

export default function JargonJournalPage() {
  const { tr } = useLang();
  const [filtres, setFiltres] = useState({ user_email: "", term: "", date_from: "", date_to: "" });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refus, setRefus] = useState("");

  const charger = useCallback(async () => {
    setLoading(true);
    try {
      const { data: reponse } = await axios.get(`${API}/api/jargon/journal`, {
        params: { ...filtres, limit: 200 },
        withCredentials: true,
      });
      setData(reponse);
      setRefus("");
    } catch (e) {
      const code = e?.response?.status;
      if (code === 403) {
        // Message explicite : « rien ne s'affiche » laisserait croire à une
        // panne alors que c'est une question de droits.
        setRefus(tr("Ce journal est réservé aux administrateurs.", "This journal is restricted to administrators."));
      } else {
        toast.error(tr("Chargement impossible : ", "Could not load: ") + (e?.response?.data?.detail || e.message));
      }
    } finally {
      setLoading(false);
    }
  }, [filtres, tr]);

  useEffect(() => { charger(); }, [charger]);

  if (refus) {
    return (
      <div className="p-6" data-testid="jargon-journal-forbidden">
        <h1 className="text-2xl font-bold text-zinc-900">{tr("Journal du dictionnaire", "Dictionary journal")}</h1>
        <p className="mt-2 text-sm text-amber-800">{refus}</p>
      </div>
    );
  }

  const totaux = data?.totals || { by_user: [], by_document: [], by_action: [], entries: 0 };
  const lignes = data?.rows || [];

  return (
    <div className="p-6 max-w-6xl mx-auto" data-testid="jargon-journal-page">
      <h1 className="text-2xl font-bold text-zinc-900">{tr("Journal du dictionnaire", "Dictionary journal")}</h1>
      <p className="text-sm text-zinc-600 mt-1">
        {tr("Qui a contribué quoi, et depuis quel document.", "Who contributed what, and from which document.")}{" "}
        {tr(`${totaux.entries} mouvement(s) retenu(s).`, `${totaux.entries} movement(s) kept.`)}
      </p>

      <div className="mt-5 grid gap-3 md:grid-cols-3">
        <Carte titre={tr("Par personne", "By person")} lignes={totaux.by_user} testid="journal-totals-user"
               libelleTerme={tr("terme(s)", "term(s)")}
               vide={tr("Aucune contribution sur cette période.", "No contribution over this period.")} />
        <Carte titre={tr("Par document source", "By source document")} lignes={totaux.by_document} testid="journal-totals-document"
               libelleTerme={tr("terme(s)", "term(s)")}
               vide={tr("Aucun document rattaché à un mouvement.", "No document attached to a movement.")} />
        <Carte titre={tr("Par action", "By action")} lignes={totaux.by_action} testid="journal-totals-action"
               vide={tr("Aucune action enregistrée.", "No action recorded.")} />
      </div>

      <div className="mt-6 flex flex-wrap items-end gap-3">
        <label className="text-xs font-medium text-zinc-700">
          {tr("Personne", "Person")}
          <input
            value={filtres.user_email}
            onChange={(e) => setFiltres((f) => ({ ...f, user_email: e.target.value }))}
            placeholder={tr("courriel ou fragment", "email or fragment")}
            className="block mt-1 text-sm border border-zinc-300 rounded-md px-2 py-1.5"
            data-testid="journal-filter-user"
          />
        </label>
        <label className="text-xs font-medium text-zinc-700">
          {tr("Terme", "Term")}
          <input
            value={filtres.term}
            onChange={(e) => setFiltres((f) => ({ ...f, term: e.target.value }))}
            className="block mt-1 text-sm border border-zinc-300 rounded-md px-2 py-1.5"
            data-testid="journal-filter-term"
          />
        </label>
        <label className="text-xs font-medium text-zinc-700">
          {tr("Du", "From")}
          <input
            type="date"
            value={filtres.date_from}
            onChange={(e) => setFiltres((f) => ({ ...f, date_from: e.target.value }))}
            className="block mt-1 text-sm border border-zinc-300 rounded-md px-2 py-1.5"
            data-testid="journal-filter-from"
          />
        </label>
        <label className="text-xs font-medium text-zinc-700">
          {tr("Au", "To")}
          <input
            type="date"
            value={filtres.date_to}
            onChange={(e) => setFiltres((f) => ({ ...f, date_to: e.target.value }))}
            className="block mt-1 text-sm border border-zinc-300 rounded-md px-2 py-1.5"
            data-testid="journal-filter-to"
          />
        </label>
        <button
          onClick={() => setFiltres({ user_email: "", term: "", date_from: "", date_to: "" })}
          className="px-3 py-2 rounded-lg border border-zinc-300 bg-white hover:bg-zinc-100 text-xs font-bold"
          data-testid="journal-filter-reset"
        >
          {tr("Réinitialiser", "Reset")}
        </button>
      </div>

      <div className="mt-4 overflow-x-auto rounded-xl border border-zinc-200 bg-white">
        <table className="min-w-full text-xs">
          <thead className="bg-zinc-50 text-zinc-600">
            <tr>
              <th className="text-left font-bold px-3 py-2">{tr("Date", "Date")}</th>
              <th className="text-left font-bold px-3 py-2">{tr("Personne", "Person")}</th>
              <th className="text-left font-bold px-3 py-2">{tr("Terme", "Term")}</th>
              <th className="text-left font-bold px-3 py-2">{tr("Action", "Action")}</th>
              <th className="text-left font-bold px-3 py-2">{tr("Champs", "Fields")}</th>
              <th className="text-left font-bold px-3 py-2">{tr("Document source", "Source document")}</th>
            </tr>
          </thead>
          <tbody data-testid="journal-rows">
            {loading && lignes.length === 0 && (
              <tr><td colSpan={6} className="px-3 py-4 text-zinc-500">{tr("Chargement…", "Loading…")}</td></tr>
            )}
            {!loading && lignes.length === 0 && (
              <tr data-testid="journal-empty">
                <td colSpan={6} className="px-3 py-4 text-zinc-500">
                  {tr("Aucun mouvement pour ces filtres.", "No movement for these filters.")}
                </td>
              </tr>
            )}
            {lignes.map((l) => (
              <tr key={l.id} className="border-t border-zinc-100" data-testid={`journal-row-${l.id}`}>
                <td className="px-3 py-2 whitespace-nowrap text-zinc-700">
                  {(l.changed_at || "").slice(0, 16).replace("T", " ")}
                </td>
                <td className="px-3 py-2 text-zinc-800">{l.user_name || l.user_email || "—"}</td>
                <td className="px-3 py-2 font-medium text-zinc-900">{l.term || "—"}</td>
                <td className="px-3 py-2 text-zinc-700">{l.operation_label}</td>
                <td className="px-3 py-2 text-zinc-500">{(l.fields_changed || []).join(", ") || "—"}</td>
                <td className="px-3 py-2">
                  {l.document_id ? (
                    <a
                      href={`/dashboard/video/${l.document_id}`}
                      className="text-[#002FA7] underline"
                      data-testid={`journal-doc-link-${l.id}`}
                    >
                      {l.document_title || l.document_id.slice(0, 8)}
                    </a>
                  ) : (
                    <span className="text-zinc-400">—</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

